import { Clock, Bed, Calculator, ChevronRight } from "lucide-react";
import CategoryPageShell from "../../../components/workspace/CategoryPageShell";
import { Para } from "../../../components/workspace/HubText";
import CarerPricingCalculator from "../../../components/CarerPricingCalculator";

export default function PricingIncome() {
  return (
    <CategoryPageShell
      testId="pcc-category-pricing-income"
      slug="pricing-income"
      eyebrow="Pricing & Income"
      title="Know your worth. Set your rates with confidence."
      description="Three tools for three different situations — pick the one that matches how you're being paid, not the one at the top of the list."
    >
      <section id="hourly-rate-calculator" className="scroll-mt-24 min-w-0" data-testid="pro-tool-hourly-rate-section">
        <a
          href="/workspace/tools/hourly-care-rate"
          className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-purple hover:shadow-md transition-all block"
          data-testid="pro-tool-hourly-rate-card"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-semibold text-lg">Hourly Care Rate Calculator</h3>
              <p className="mt-1 text-xs font-semibold text-purple uppercase tracking-wide">Use this for: hourly or visiting care, paid by the hour</p>
              <p className="mt-2 text-ink-muted text-sm leading-relaxed">
                Plan an hourly rate that covers your income target, business costs, mileage and the unpaid admin time behind every placement.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-purple text-sm font-medium">
                Open calculator
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
              </span>
            </div>
          </div>
        </a>
      </section>

      <section id="live-in-rate-calculator" className="scroll-mt-24 min-w-0" data-testid="pro-tool-livein-rate-section">
        <a
          href="/workspace/tools/live-in-care-rate"
          className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-purple hover:shadow-md transition-all block"
          data-testid="pro-tool-livein-rate-card"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center shrink-0">
              <Bed className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-semibold text-lg">Live-In Care Rate Calculator</h3>
              <p className="mt-1 text-xs font-semibold text-purple uppercase tracking-wide">Use this for: live-in placements — builds up a day rate from daytime hours + waking nights</p>
              <p className="mt-2 text-ink-muted text-sm leading-relaxed">
                Price a live-in placement around active daytime hours, agreed breaks and rest, with a separate allowance for waking nights.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-purple text-sm font-medium">
                Open calculator
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
              </span>
            </div>
          </div>
        </a>
      </section>

      <section id="carer-pricing-calculator" className="scroll-mt-24 min-w-0" data-testid="pro-pricing-calculator-section">
        <div className="max-w-3xl mb-5">
          <div className="overline text-teal mb-2 flex items-center gap-2">
            <Calculator className="w-4 h-4" strokeWidth={2} /> Carer Pricing Calculator
          </div>
          <p className="text-xs font-semibold text-teal uppercase tracking-wide">Use this for: working backwards from a monthly take-home goal to a day rate</p>
          <Para>
            Rather than building a rate up from hours, this tool starts from the monthly income you want and works
            backwards to a day rate across the days you actually work. It's a different way of arriving at a similar
            number to the Live-In calculator above — useful if you think in terms of "what I need to earn" rather
            than "what my hours cost". It is not tax, legal or financial advice.
          </Para>
        </div>
        <CarerPricingCalculator />
      </section>
    </CategoryPageShell>
  );
}
